import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_EVALUATION_RATINGS } from '@/api/endpoints';
import { EVALUATION_RATINGS_QUERY_KEY } from '@/api/queryKeys';
import { useStudyYearPeriods } from '@/pages/Overview/hooks/useStudyYearPeriods';
import type { StudyYearPeriod } from '@/types/overview';
import type { EvaluationChart } from '@/types/ratings';
import { unwrapAxiosResponse } from '@/utils/axios';
import { toApiDate } from '@/utils/date';

export interface RatingsFilters {
  period: StudyYearPeriod;
  includeSameLevelClasses: boolean;
  sameLevelClassIds: number[];
  lessonTypeId: number | null;
}

interface Options {
  filters: RatingsFilters;
  enabled: boolean;
}

export const useEvaluationRatings = ({ filters, enabled }: Options) => {
  const { period, includeSameLevelClasses, sameLevelClassIds, lessonTypeId } =
    filters;
  const { arePeriodsLoading } = useStudyYearPeriods();
  const from = toApiDate(period.from);
  const to = toApiDate(period.to);
  const classIds = includeSameLevelClasses ? sameLevelClassIds : [];

  return useQuery<EvaluationChart[]>({
    queryKey: [
      ...EVALUATION_RATINGS_QUERY_KEY,
      from,
      to,
      includeSameLevelClasses,
      classIds,
      lessonTypeId,
    ],
    enabled: enabled && !arePeriodsLoading,
    queryFn: () =>
      apiClient
        .get<EvaluationChart[]>(API_EVALUATION_RATINGS, {
          params: {
            includeSameLevelClasses,
            'datePeriodModel.from': from,
            'datePeriodModel.to': to,
            ...(lessonTypeId !== null ? { lessonTypeId } : {}),
            ...(classIds.length > 0 ? { sameLevelClassIds: classIds } : {}),
          },
          paramsSerializer: { indexes: null },
        })
        .then(unwrapAxiosResponse),
  });
};
